<!--
// app/components/Shared/SliderWrapper.vue
-->
<script setup lang="ts">
import LeftArrow from "~/app/components/Shared/icons/LeftArrow.vue";
import RightArrow from "~/app/components/Shared/icons/RightArrow.vue";
import { useTouchDevice } from "~/app/composables/device/useTouchDevice";

/**
 * SliderWrapper Component
 *
 * A reusable wrapper component that adds arrow navigation to horizontal scroll containers.
 * Designed for non-touch devices with intelligent arrow visibility based on scroll position.
 *
 * @example
 * <SliderWrapper
 *   :outer-arrows="false"
 *   :scroll-distance="300"
 *   :enable-bounce-animation="true"
 *   auto-scroll-selector=".card-match.live-match, .card-match[data-last-played]"
 * >
 *   <div class="flex gap-4 overflow-x-auto">
 *     <!-- Your scrollable content here -->
 *   </div>
 * </SliderWrapper>
 */

type Props = {
  /**
   * Whether to position arrows outside the container (true) or inside overlaying content (false)
   * @default false
   */
  outerArrows?: boolean;

  /**
   * Distance in pixels to scroll when clicking arrows.
   * If not specified, defaults to 50% of the visible container width
   */
  scrollDistance?: number;

  /**
   * Enable bounce/swing animation on mount to hint at scrollable content
   * Only plays if slider is at the start position
   * @default false
   */
  enableBounceAnimation?: boolean;

  /**
   * CSS selector(s) for auto-scrolling to a specific element on mount
   * Supports multiple selectors separated by commas (tries in order)
   * @example ".live-match, .featured-item"
   */
  autoScrollSelector?: string;

  /**
   * Whether to hide arrows on touch devices
   * @default true
   */
  disableOnTouch?: boolean;

  /**
   * CSS selector for the scrollable element inside the slot
   * If provided, skips automatic detection for better performance
   * @example ".matches-slider-wrapper"
   */
  scrollableSelector?: string;
};

const props = withDefaults(defineProps<Props>(), {
  outerArrows: false,
  scrollDistance: undefined,
  enableBounceAnimation: false,
  autoScrollSelector: undefined,
  disableOnTouch: true,
});

// Touch device detection
const { isTouchDevice } = useTouchDevice();

// Template refs
const sliderContainer = ref<HTMLElement | null>(null);
const sliderContent = ref<HTMLElement | null>(null);
const actualScrollableElement = ref<HTMLElement | null>(null);

// Arrow visibility state
const showLeftArrow = ref<boolean>(false);
const showRightArrow = ref<boolean>(false);

// Animation state
const shouldAnimate = ref<boolean>(false);

// Auto-scrolling state - used to hide content during initial auto-scroll
// Initialize to true if autoScrollSelector is provided
const isAutoScrolling = ref<boolean>(!!props.autoScrollSelector);

// RTL detection
const isRTL = ref<boolean>(false);

// Active intervals tracking
const activeIntervals = ref<Set<NodeJS.Timeout>>(new Set());

/**
 * Check if arrows should be displayed
 */
const shouldShowArrows = computed(() => {
  if (props.disableOnTouch && isTouchDevice.value) {
    return false;
  }
  return true;
});

/**
 * Update arrow visibility based on scroll position
 */
const updateArrowVisibility = () => {
  const container = actualScrollableElement.value;

  if (!container) return;

  const scrollLeft = Math.round(container.scrollLeft);
  const scrollWidth = Math.round(container.scrollWidth);
  const clientWidth = Math.round(container.clientWidth);

  // Check if content is scrollable
  const isScrollable = scrollWidth > clientWidth;

  if (!isScrollable) {
    showLeftArrow.value = false;
    showRightArrow.value = false;
    return;
  }

  // For LTR
  if (!isRTL.value) {
    // Show left arrow if not at start
    showLeftArrow.value = scrollLeft > 0;

    // Show right arrow if not at end
    showRightArrow.value = scrollLeft < scrollWidth - clientWidth;
  } else {
    // For RTL (scroll positions are negative or reversed depending on browser)
    const isAtStart = scrollLeft === 0 || Math.abs(scrollLeft) < 1;
    const isAtEnd = Math.abs(scrollLeft) >= scrollWidth - clientWidth - 1;

    showLeftArrow.value = !isAtEnd;
    showRightArrow.value = !isAtStart;
  }
};

/**
 * Perform smooth scroll animation
 */
const performScroll = (direction: "left" | "right") => {
  if (!actualScrollableElement.value) return;

  // Clear any active scroll intervals
  activeIntervals.value.forEach(interval => clearInterval(interval));
  activeIntervals.value.clear();

  const container = actualScrollableElement.value;

  // Calculate scroll distance
  const distance = props.scrollDistance ?? Math.round(container.clientWidth / 2);

  // Determine scroll direction based on LTR/RTL
  let scrollDelta = distance;
  if (direction === "left") {
    scrollDelta = -distance;
  }

  // Smooth scroll animation
  const speed = 15; // milliseconds per step
  const step = Math.round(distance / 20); // 20 steps total
  let scrollAmount = 0;

  const slideTimer = setInterval(() => {
    if (!container) {
      clearInterval(slideTimer);
      activeIntervals.value.delete(slideTimer);
      return;
    }

    container.scrollLeft += scrollDelta > 0 ? step : -step;
    scrollAmount += step;

    if (scrollAmount >= Math.abs(scrollDelta)) {
      clearInterval(slideTimer);
      activeIntervals.value.delete(slideTimer);
      updateArrowVisibility();
    }
  }, speed);

  activeIntervals.value.add(slideTimer);
};

/**
 * Handle left arrow click
 */
const handleLeftClick = () => {
  performScroll("left");
  // Disable animation after user interaction
  shouldAnimate.value = false;
};

/**
 * Handle right arrow click
 */
const handleRightClick = () => {
  performScroll("right");
  // Disable animation after user interaction
  shouldAnimate.value = false;
};

/**
 * Auto-scroll to target element on mount
 * Returns true if auto-scroll was performed, false otherwise
 */
const autoScrollToElement = (): boolean => {
  if (!props.autoScrollSelector || !actualScrollableElement.value) {
    return false;
  }

  const container = actualScrollableElement.value;

  // Split selectors by comma and try each one
  const selectors = props.autoScrollSelector.split(",").map(s => s.trim());

  let targetElement: HTMLElement | null = null;

  for (const selector of selectors) {
    targetElement = container.querySelector(selector);
    if (targetElement) break;
  }

  if (!targetElement) {
    isAutoScrolling.value = false;
    return false;
  }

  // Calculate scroll position directly without resetting to 0 first
  // This avoids the visual "shift" by scrolling instantly to the target position
  const targetX = targetElement.getBoundingClientRect().x;
  const containerX = container.getBoundingClientRect().x;
  const offset = targetX - containerX;

  // Adjust for different screen sizes (similar to SliderMatches.vue)
  let scrollPosition: number;
  if (window.innerWidth < 1200) {
    // Mobile: scroll to element with small offset
    scrollPosition = offset - 24;
  } else {
    // Desktop: scroll to show element in better position
    scrollPosition = offset - 324;
  }

  // Set scroll position instantly (no animation, no intermediate state)
  container.scrollLeft = scrollPosition;

  // Update arrow visibility and show content after scroll
  setTimeout(() => {
    updateArrowVisibility();
    isAutoScrolling.value = false;
  }, 100);

  return true; // Auto-scroll was performed
};

/**
 * Initialize bounce animation if enabled
 */
const initializeBounceAnimation = () => {
  if (!props.enableBounceAnimation || !actualScrollableElement.value) return;

  const container = actualScrollableElement.value;

  // Only animate if at the start position and hasn't been auto-scrolled
  const hasScrolled = container.scrollLeft !== 0;

  if (!hasScrolled) {
    shouldAnimate.value = true;

    // Remove animation after it completes (2 seconds)
    setTimeout(() => {
      shouldAnimate.value = false;
    }, 2000);
  }
};

/**
 * Find the actual scrollable element
 * If scrollableSelector prop is provided, uses it for direct selection (faster)
 * Otherwise, performs automatic detection
 */
const getScrollableElement = (): HTMLElement | null => {
  if (!sliderContent.value) return null;

  const slotElement = sliderContent.value;

  // Fast path: use provided selector if available
  if (props.scrollableSelector) {
    const scrollableChild = slotElement.querySelector<HTMLElement>(props.scrollableSelector);
    if (scrollableChild) {
      return scrollableChild;
    }
  }

  // Fallback: automatic detection (slower)
  // First, check if the slot content itself is scrollable
  const hasOverflow = slotElement.scrollWidth > slotElement.clientWidth;

  if (hasOverflow) {
    return slotElement;
  }

  // If not, look for a child with overflow-x-auto or overflow-x-scroll
  const scrollableChild = slotElement.querySelector<HTMLElement>(
    '[style*="overflow-x"], .overflow-x-auto, .overflow-x-scroll'
  );

  if (scrollableChild) {
    return scrollableChild;
  }

  // Fallback: find any child that has scrollable content
  const children = Array.from(slotElement.children) as HTMLElement[];
  for (const child of children) {
    if (child.scrollWidth > child.clientWidth) {
      return child;
    }
  }

  return slotElement;
};

/**
 * Initialize the slider - can be called manually to re-initialize
 */
const initializeSlider = () => {
  // Reset animation state at the beginning
  shouldAnimate.value = false;

  // Find the actual scrollable element
  const scrollableElement = getScrollableElement();
  if (scrollableElement) {
    // Store the actual scrollable element
    actualScrollableElement.value = scrollableElement;
  }

  // Initial arrow visibility check
  updateArrowVisibility();

  // Setup scroll listener (remove old one first if exists)
  if (actualScrollableElement.value) {
    actualScrollableElement.value.removeEventListener("scroll", updateArrowVisibility);
    actualScrollableElement.value.addEventListener("scroll", updateArrowVisibility);
  }

  // Auto-scroll to element if specified (this should happen BEFORE animation)
  const didAutoScroll = autoScrollToElement();

  // Initialize bounce animation only if we didn't auto-scroll
  if (!didAutoScroll) {
    // Wait a bit to ensure content is fully rendered
    setTimeout(() => {
      initializeBounceAnimation();
    }, 150);
  }
};

// Lifecycle hooks
onMounted(() => {
  // Detect RTL
  isRTL.value = document.documentElement.dir === "rtl";

  // Wait for next tick to ensure slot content is rendered
  nextTick(() => {
    initializeSlider();

    // Re-check arrow visibility on window resize
    window.addEventListener("resize", updateArrowVisibility);
  });
});

// Expose methods for parent components
defineExpose({
  initializeSlider,
  updateArrowVisibility,
});

onUnmounted(() => {
  // Clear all active intervals
  activeIntervals.value.forEach(interval => clearInterval(interval));
  activeIntervals.value.clear();

  // Remove scroll event listener
  if (actualScrollableElement.value) {
    actualScrollableElement.value.removeEventListener("scroll", updateArrowVisibility);
  }

  // Remove resize listener
  window.removeEventListener("resize", updateArrowVisibility);
});
</script>

<template>
  <div ref="sliderContainer" class="slider-wrapper">
    <!-- Left Arrow - Client Only (not rendered in SSR) -->
    <ClientOnly>
      <button
        v-if="shouldShowArrows && showLeftArrow"
        data-ignore-heatmap
        class="slide-button-left"
        :class="[outerArrows ? '-left-20 top-1/3' : 'left-6 top-1/2']"
        @click="handleLeftClick"
      >
        <left-arrow width="30" height="30"></left-arrow>
      </button>
    </ClientOnly>

    <!-- Slider Content - Rendered in SSR for SEO -->
    <div
      ref="sliderContent"
      class="slider-content"
      :class="{
        'slider-bounce-animation': shouldAnimate,
        'blur-sm pointer-events-none': isAutoScrolling,
        'blur-none': !isAutoScrolling,
      }"
    >
      <slot />
    </div>

    <!-- Right Arrow - Client Only (not rendered in SSR) -->
    <ClientOnly>
      <button
        v-if="shouldShowArrows && showRightArrow"
        data-ignore-heatmap
        class="slide-button-right"
        :class="[outerArrows ? '-right-20 top-1/3' : 'right-6 top-1/2']"
        @click="handleRightClick"
      >
        <right-arrow width="30" height="30"></right-arrow>
      </button>
    </ClientOnly>
  </div>
</template>

<style scoped>
.slider-wrapper {
  @apply relative w-full h-full;
}

.slider-content {
  @apply w-full h-full;
  transition: filter 0.3s ease-in-out;
}

.slider-bounce-animation {
  animation: slider-bounce-hint 2s ease-in-out;
}
</style>
